import type { StripePlan } from '@/lib/stripe-config';

// ============================================
// Tier Types (re-exported for convenience)
// Core definitions are in subscription-rules.ts
// ============================================

/**
 * Subscription tier identifier
 */
export type TierId = 'free' | 'pro' | 'business' | 'family';

/**
 * Beneficiary policy for a tier
 */
export type BeneficiaryPolicy = 'self-only' | 'any';

/**
 * Subscription tier configuration
 */
export interface SubscriptionTierConfig {
  id: TierId;
  name: string;
  canHaveSpecialPrice: boolean;
  ticketLimit: number | null; // null = unlimited
  beneficiaryPolicy: BeneficiaryPolicy;
  monthlyPriceCents: number;
  annualPriceCents: number;
  features: string[];
}

// ============================================
// Subscription Status Types
// ============================================

/**
 * Subscription status from Stripe
 */
export type SubscriptionStatus = 
  | 'active' 
  | 'canceled' 
  | 'past_due' 
  | 'trialing' 
  | 'incomplete' 
  | 'incomplete_expired' 
  | 'unpaid'
  | null; // Free tier (no subscription)

/**
 * VOY-554: the ONE list of subscription statuses that count as paid (member pricing, plan shown as
 * current). Same as the backend's PAID_STATUSES. past_due is NOT paid: Stripe is retrying the card.
 */
export const PAID_STATUSES: readonly SubscriptionStatus[] = Object.freeze(['active', 'trialing'] as const);

/** Whether a subscription status counts as paid (see PAID_STATUSES). */
export function isPaidStatus(status: SubscriptionStatus | string | null | undefined): boolean {
  return !!status && (PAID_STATUSES as readonly string[]).includes(status);
}

// ============================================
// Raw Subscription Data
// ============================================

/**
 * Raw subscription data from stripe_user_subscriptions table
 */
export interface UserSubscription {
  user_id: string;
  subscription_id: string;
  price_id: string;
  // VOY-554: the price's tier from subscription_prices (joined by the stripe_user_subscriptions
  // view); null when the price is not in the table (a data integrity failure).
  tier_id?: TierId | null;
  status: SubscriptionStatus;
  // bigint Unix seconds in stripe_user_subscriptions; older rows may carry an ISO string.
  current_period_end: number | string | null;
  current_period_start: number | string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================
// Display Data Types
// ============================================

/**
 * Formatted subscription data ready for display
 */
export interface SubscriptionDisplayData {
  // Plan identification
  planName: string;
  planKey: StripePlan | null;
  
  // Tier information (for paywall)
  tier: TierId;
  tierName: string;
  canHaveSpecialPrice: boolean;
  ticketLimit: number | null;
  beneficiaryPolicy: BeneficiaryPolicy;
  
  // Status
  status: SubscriptionStatus;
  statusLabel: string;
  statusColor: 'green' | 'yellow' | 'red' | 'blue' | 'gray';
  
  // Dates
  renewalDate: Date | null;
  renewalDateFormatted: string | null;
  
  // Pricing
  price: number;
  priceFormatted: string;
  
  // Features
  benefits: string[];
  
  // Flags
  isActive: boolean;
  isTrial: boolean;
  isCancelled: boolean;
  isFreeTier: boolean;
  cancelAtPeriodEnd: boolean;
  isAnnual: boolean; // Whether subscription is billed annually
}

// ============================================
// Default Tier Configurations
// ============================================

export const DEFAULT_TIER_CONFIGS: Record<TierId, SubscriptionTierConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    canHaveSpecialPrice: false,
    ticketLimit: null,
    beneficiaryPolicy: 'any',
    monthlyPriceCents: 0,
    annualPriceCents: 0,
    features: ['Standard pricing', 'Unlimited searches', 'Basic support'],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'self-only',
    monthlyPriceCents: 300,
    annualPriceCents: 3000,
    features: ['Special pricing', 'Up to 8 passengers'],
  },
  business: {
    id: 'business',
    name: 'Business',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'self-only',
    monthlyPriceCents: 700,
    annualPriceCents: 7000,
    features: ['Special pricing', 'Up to 8 passengers'],
  },
  family: {
    id: 'family',
    name: 'Family',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'any',
    monthlyPriceCents: 1000,
    annualPriceCents: 10000,
    features: ['Special pricing', 'Up to 8 passengers', 'Book for anyone'],
  },
};

/**
 * Get tier configuration by ID
 */
export function getTierConfig(tierId: TierId): SubscriptionTierConfig {
  return DEFAULT_TIER_CONFIGS[tierId] || DEFAULT_TIER_CONFIGS.free;
}
